import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';

// ── Number Pool (/api/pool) ───────────────────────────────────────────

export type PoolNumberRecord = { phone_number: string; campaign_id?: string | null; allocated_at?: string };
export type PoolStats = { total: number; available: number; allocated: number };

/** `POST /api/pool/add` — add tracking numbers to the pool. */
export async function addPoolNumbers(phoneNumbers: string[], axiosInstance?: AxiosInstance): Promise<{ ok: boolean; added: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.pool}/add`, { phone_numbers: phoneNumbers });
  return { ok: true, added: response.data?.added ?? phoneNumbers.length };
}

/** `POST /api/pool/allocate` — allocate a number from the pool for a campaign. */
export async function allocatePoolNumber(campaignId: string, axiosInstance?: AxiosInstance): Promise<PoolNumberRecord> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.pool}/allocate`, { campaign_id: campaignId });
  return { phone_number: String(response.data?.phone_number ?? ''), campaign_id: campaignId, allocated_at: response.data?.allocated_at };
}

/** `POST /api/pool/release` — release a number back to the pool. */
export async function releasePoolNumber(phoneNumber: string, axiosInstance?: AxiosInstance): Promise<{ ok: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.post(`${apiConfig.voiceEndpoints.pool}/release`, { phone_number: phoneNumber });
  return { ok: true };
}

/** `GET /api/pool/lookup/{phone_number}` — look up campaign allocation for a number. */
export async function lookupPoolNumber(phoneNumber: string, axiosInstance?: AxiosInstance): Promise<PoolNumberRecord | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const encoded = encodeURIComponent(phoneNumber);
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.pool}/lookup/${encoded}`);
  if (!response.data) return null;
  const d = response.data;
  return { phone_number: String(d.phone_number ?? phoneNumber), campaign_id: d.campaign_id ?? null, allocated_at: d.allocated_at };
}

/** `GET /api/pool/stats` — pool availability statistics. */
export async function fetchPoolStats(axiosInstance?: AxiosInstance): Promise<PoolStats> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<PoolStats>(`${apiConfig.voiceEndpoints.pool}/stats`);
  return response.data;
}

/** Pre-save validation: `POST /api/integrations/calcom/test`. */
export async function testCalcomConnection(
  apiKey: string,
  axiosInstance?: AxiosInstance
): Promise<{ ok: boolean; user?: { username?: string; email?: string; name?: string; timeZone?: string }; error?: string | null }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.integrations}/calcom/test`;
  const response = await client.post<{ ok: boolean; user?: any; error?: string | null }>(url, { api_key: apiKey });
  return response.data;
}

/** Save Cal.com integration via `POST /api/integrations/connect`.
 *  Accepts a raw numeric event-type ID or a full cal.com URL; the numeric ID is extracted. */
export async function connectCalcomIntegration(
  input: { apiKey: string; eventTypeId: string; timezone?: string; name?: string },
  axiosInstance?: AxiosInstance
): Promise<{ ok: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const raw = input.eventTypeId.trim();
  const match = raw.match(/event-types\/(\d+)/) ?? raw.match(/(\d+)/);
  const eventTypeId = match ? parseInt(match[1], 10) : 0;
  await client.post(`${apiConfig.voiceEndpoints.integrations}/connect`, {
    integration_id: 'calcom',
    name: input.name || 'Cal.com Booking',
    credentials: {
      api_key: input.apiKey,
      event_type_id: eventTypeId,
      timezone: input.timezone || 'America/Chicago',
    },
    config: {},
  });
  return { ok: true };
}
